import React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cn } from "@/utils/utils";
import Text, { TextVariant } from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

export interface CheckmarkProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    "onCheckedChange"
  > {
  label?: string;
  labelVariant?: TextVariant;
  onCheckedChange?: (checked: boolean) => void;
  containerClassName?: string;
  labelClassName?: string;
  size?: "sm" | "md" | "lg";
  tooltipText?: string;
}

const Checkmark = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckmarkProps
>(
  (
    {
      className,
      label,
      labelVariant = "body",
      id,
      onCheckedChange,
      checked,
      disabled,
      containerClassName,
      labelClassName,
      size = "md",
      tooltipText,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6"
    };

    const iconSizeClasses = {
      sm: "h-3 w-3",
      md: "h-4 w-4",
      lg: "h-5 w-5"
    };

    const checkboxElement = (
      <div
        className={cn(
          "p-1.5 rounded-full flex items-center justify-center transition-colors duration-200",
          !disabled && "hover:bg-surface-hover-strong"
        )}
      >
        <CheckboxPrimitive.Root
          ref={ref}
          id={id}
          className={cn(
            "peer shrink-0 rounded-full border ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200",
            "border-secondary bg-transparent",
            checked && "border-positive",
            disabled && "cursor-not-allowed opacity-50",
            sizeClasses[size],
            className
          )}
          checked={checked}
          onCheckedChange={(newChecked) =>
            !disabled && onCheckedChange?.(newChecked === true)
          }
          {...props}
        >
          <CheckboxPrimitive.Indicator
            className={cn(
              "flex items-center justify-center",
              checked && "text-positive"
            )}
          >
            <Check className={iconSizeClasses[size]} strokeWidth="1" />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      </div>
    );

    const handleClick = (event: React.MouseEvent) => {
      event.stopPropagation();
    };

    return (
      <div
        className={cn(
          "flex items-center gap-2",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          containerClassName
        )}
        onClick={handleClick}
      >
        {tooltipText ? (
          <Tooltip>
            <TooltipTrigger asChild>{checkboxElement}</TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        ) : (
          checkboxElement
        )}
        {label && (
          <label
            htmlFor={id}
            className={cn(
              "min-w-0 break-words",
              disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
              labelClassName
            )}
          >
            <Text variant={labelVariant}>{label}</Text>
          </label>
        )}
      </div>
    );
  }
);

Checkmark.displayName = "Checkmark";

export default Checkmark;
